package pl.nickers.match3.ui

import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Star
import androidx.compose.material.icons.filled.StarBorder
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.jetbrains.compose.resources.painterResource
import pl.nickers.match3.DevEntityType
import pl.nickers.match3.GridPos
import pl.nickers.match3.JellyCell
import pl.nickers.match3.ecs.LaserDirection

private val devEntityLabels = listOf(
    DevEntityType.JELLY_1 to "Ember",
    DevEntityType.JELLY_2 to "Moon",
    DevEntityType.JELLY_3 to "Herb",
    DevEntityType.JELLY_4 to "Pollen",
    DevEntityType.JELLY_5 to "Petal",
    DevEntityType.JELLY_6 to "Nectar",
    DevEntityType.BOMB to "Bomb",
    DevEntityType.CROSS_BOMB to "X-Bomb",
    DevEntityType.LASER_H to "LaserH",
    DevEntityType.LASER_V to "LaserV",
    DevEntityType.COLOURFUL_SWIRL to "Swirl",
    DevEntityType.ICE_CUBE to "Amber",
    DevEntityType.SPAWN_POINT to "Spawn",
)

private val devEntityBodyImages = mapOf(
    DevEntityType.JELLY_1 to "jelly_1.png",
    DevEntityType.JELLY_2 to "jelly_2.png",
    DevEntityType.JELLY_3 to "jelly_3.png",
    DevEntityType.JELLY_4 to "jelly_4.png",
    DevEntityType.JELLY_5 to "jelly_5.png",
    DevEntityType.JELLY_6 to "jelly_6.png",
    DevEntityType.BOMB to "bomb.png",
    DevEntityType.CROSS_BOMB to "cross_bomb.png",
    DevEntityType.LASER_H to "laser.png",
    DevEntityType.LASER_V to "laser.png",
    DevEntityType.COLOURFUL_SWIRL to "swirl.png",
    DevEntityType.ICE_CUBE to "ice_3.png",
)

@Composable
internal fun LostDialog(
    onTryAgain: () -> Unit,
) {
    AlertDialog(
        onDismissRequest = {},
        title = {
            Text(
                text = "Brew Failed!",
                fontWeight = FontWeight.Bold,
                fontSize = 28.sp,
            )
        },
        text = {
            Text(text = "You ran out of moves before the potion was ready.")
        },
        confirmButton = {
            Button(onClick = onTryAgain) { Text("Try Again") }
        },
    )
}

@Composable
internal fun WinDialog(
    score: Int,
    stars: Int = 0,
    isNewStarBest: Boolean = false,
    isJourneyMode: Boolean = false,
    isLastJourneyLevel: Boolean = false,
    isTestLevel: Boolean = false,
    onNewGame: () -> Unit,
    onNextLevel: () -> Unit = {},
    onBackToJourney: () -> Unit = {},
) {
    AlertDialog(
        onDismissRequest = {},
        title = {
            Text(
                text = "Potion Brewed!",
                fontWeight = FontWeight.Bold,
                fontSize = 28.sp,
            )
        },
        text = {
            Column(
                modifier = Modifier.fillMaxWidth(),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                // Stars are a journey-mode concept; arcade and test-level wins just show the score.
                if (isJourneyMode && !isTestLevel) {
                    StarRow(stars = stars)
                    if (isNewStarBest) {
                        Spacer(Modifier.height(8.dp))
                        Text(
                            text = "★ New best!",
                            color = MaterialTheme.colorScheme.primary,
                            fontWeight = FontWeight.SemiBold,
                        )
                    }
                    Spacer(Modifier.height(8.dp))
                }
                Text(text = "Score: $score")
            }
        },
        confirmButton = {
            when {
                isTestLevel -> Button(onClick = onBackToJourney) { Text("Back to Test levels") }
                !isJourneyMode -> Button(onClick = onNewGame) { Text("New Game") }
                isLastJourneyLevel -> Button(onClick = onBackToJourney) { Text("Back to Journey") }
                else -> Button(onClick = onNextLevel) { Text("Next Level") }
            }
        },
    )
}

/** Three star slots (filled gold when earned, dimmed outlines otherwise). */
@Composable
private fun StarRow(stars: Int) {
    Row(
        horizontalArrangement = Arrangement.spacedBy(6.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        repeat(3) { i ->
            val earned = i < stars
            Icon(
                imageVector = if (earned) Icons.Filled.Star else Icons.Filled.StarBorder,
                contentDescription = if (earned) "Star earned" else "Star not earned",
                tint = if (earned) Color(0xFFFFC107) else MaterialTheme.colorScheme.onSurface.copy(alpha = 0.3f),
                modifier = Modifier.size(40.dp),
            )
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
internal fun EditEntityDialog(
    selectedPos: GridPos,
    cell: JellyCell?,
    enabled: Boolean,
    isHole: Boolean,
    hasTeleportTarget: Boolean,
    onReplace: (DevEntityType) -> Unit,
    onDelete: () -> Unit,
    onSetIceCubeLife: (Int) -> Unit,
    onSetLaserDirection: (LaserDirection) -> Unit,
    onSetSpawnPointVisible: (Boolean) -> Unit,
    onToggleHole: () -> Unit,
    onBeginTeleportLink: () -> Unit,
    onRemoveTeleportTarget: () -> Unit,
    onDismiss: () -> Unit,
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("Edit [${selectedPos.row}, ${selectedPos.col}]", fontWeight = FontWeight.Bold) },
        text = {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState()),
            ) {
                if (isHole) {
                    Text("Hole", fontWeight = FontWeight.SemiBold, fontSize = 15.sp)
                    Spacer(Modifier.height(12.dp))
                    Button(
                        onClick = { onToggleHole(); onDismiss() },
                        enabled = enabled,
                        modifier = Modifier.fillMaxWidth(),
                    ) { Text("Make Accessible") }
                    return@Column
                }

                if (cell != null) {
                    val entityLabel = when {
                        cell.isIceCube -> "Ice Cube"
                        cell.isLaser && cell.laserDirection == LaserDirection.HORIZONTAL -> "Laser — Horizontal"
                        cell.isLaser && cell.laserDirection == LaserDirection.VERTICAL -> "Laser — Vertical"
                        cell.isCrossBomb -> "Cross Bomb"
                        cell.isBomb -> "Bomb"
                        cell.isColourfulSwirl -> "Colourful Swirl"
                        cell.isSpawnPoint -> "Spawn Point"
                        else -> "Jelly ${cell.type}"
                    }
                    Text(entityLabel, fontWeight = FontWeight.SemiBold, fontSize = 15.sp)
                    Spacer(Modifier.height(8.dp))

                    if (cell.isIceCube) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Text("Life:", modifier = Modifier.width(44.dp))
                            listOf(1, 2, 3).forEach { n ->
                                val isCurrent = cell.iceCubeLife == n
                                if (isCurrent) {
                                    Button(
                                        onClick = {},
                                        modifier = Modifier.padding(end = 4.dp),
                                        contentPadding = androidx.compose.foundation.layout.PaddingValues(horizontal = 12.dp, vertical = 4.dp),
                                    ) { Text("$n") }
                                } else {
                                    OutlinedButton(
                                        onClick = { onSetIceCubeLife(n) },
                                        enabled = enabled,
                                        modifier = Modifier.padding(end = 4.dp),
                                        contentPadding = androidx.compose.foundation.layout.PaddingValues(horizontal = 12.dp, vertical = 4.dp),
                                    ) { Text("$n") }
                                }
                            }
                        }
                        Spacer(Modifier.height(8.dp))
                    }

                    if (cell.isLaser) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Text("Dir:", modifier = Modifier.width(44.dp))
                            listOf(LaserDirection.HORIZONTAL to "H", LaserDirection.VERTICAL to "V").forEach { (dir, label) ->
                                val isCurrent = cell.laserDirection == dir
                                if (isCurrent) {
                                    Button(
                                        onClick = {},
                                        modifier = Modifier.padding(end = 4.dp),
                                        contentPadding = androidx.compose.foundation.layout.PaddingValues(horizontal = 12.dp, vertical = 4.dp),
                                    ) { Text(label) }
                                } else {
                                    OutlinedButton(
                                        onClick = { onSetLaserDirection(dir) },
                                        enabled = enabled,
                                        modifier = Modifier.padding(end = 4.dp),
                                        contentPadding = androidx.compose.foundation.layout.PaddingValues(horizontal = 12.dp, vertical = 4.dp),
                                    ) { Text(label) }
                                }
                            }
                        }
                        Spacer(Modifier.height(8.dp))
                    }

                    if (cell.isSpawnPoint) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Text("Visible:", modifier = Modifier.width(60.dp))
                            listOf(true to "On", false to "Off").forEach { (v, label) ->
                                val isCurrent = cell.spawnPointVisible == v
                                if (isCurrent) {
                                    Button(
                                        onClick = {},
                                        modifier = Modifier.padding(end = 4.dp),
                                        contentPadding = androidx.compose.foundation.layout.PaddingValues(horizontal = 12.dp, vertical = 4.dp),
                                    ) { Text(label) }
                                } else {
                                    OutlinedButton(
                                        onClick = { onSetSpawnPointVisible(v) },
                                        enabled = enabled,
                                        modifier = Modifier.padding(end = 4.dp),
                                        contentPadding = androidx.compose.foundation.layout.PaddingValues(horizontal = 12.dp, vertical = 4.dp),
                                    ) { Text(label) }
                                }
                            }
                        }
                        Spacer(Modifier.height(8.dp))
                    }

                    HorizontalDivider()
                    Spacer(Modifier.height(8.dp))
                    Button(
                        onClick = { onDelete(); onDismiss() },
                        enabled = enabled,
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFB71C1C)),
                        modifier = Modifier.fillMaxWidth(),
                    ) { Text("Delete Entity") }
                    Spacer(Modifier.height(8.dp))
                    HorizontalDivider()
                    Spacer(Modifier.height(8.dp))
                }

                Text(
                    if (cell != null) "Replace" else "Create",
                    fontWeight = FontWeight.SemiBold,
                    fontSize = 13.sp,
                )
                Spacer(Modifier.height(6.dp))
                FlowRow(
                    horizontalArrangement = Arrangement.spacedBy(6.dp),
                    verticalArrangement = Arrangement.spacedBy(6.dp),
                ) {
                    devEntityLabels.forEach { (type, label) ->
                        val bodyImage = devEntityBodyImages[type] ?: "jelly_1.png"
                        Button(
                            onClick = { onReplace(type); onDismiss() },
                            enabled = enabled,
                            modifier = Modifier.size(52.dp),
                            shape = androidx.compose.foundation.shape.RoundedCornerShape(6.dp),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = Color(0xFF2E7D32),
                                contentColor = Color.White,
                            ),
                            contentPadding = androidx.compose.foundation.layout.PaddingValues(4.dp),
                        ) {
                            Image(
                                painter = painterResource(bodyImage.toBodyDrawableRes()),
                                contentDescription = label,
                                modifier = Modifier.fillMaxWidth(),
                                contentScale = ContentScale.Fit,
                            )
                        }
                    }
                }
                Spacer(Modifier.height(8.dp))
                HorizontalDivider()
                Spacer(Modifier.height(8.dp))
                Button(
                    onClick = { onToggleHole(); onDismiss() },
                    enabled = enabled,
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFB71C1C)),
                    modifier = Modifier.fillMaxWidth(),
                ) { Text("Make Hole") }

                Spacer(Modifier.height(8.dp))
                HorizontalDivider()
                Spacer(Modifier.height(8.dp))
                if (hasTeleportTarget) {
                    Button(
                        onClick = { onRemoveTeleportTarget(); onDismiss() },
                        enabled = enabled,
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF7E57C2)),
                        modifier = Modifier.fillMaxWidth(),
                    ) { Text("Remove Teleport Target") }
                } else {
                    Button(
                        onClick = { onBeginTeleportLink(); onDismiss() },
                        enabled = enabled,
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF7E57C2)),
                        modifier = Modifier.fillMaxWidth(),
                    ) { Text("Teleport Target → from…") }
                }
            }
        },
        confirmButton = {
            TextButton(onClick = onDismiss) { Text("Done") }
        },
    )
}

